import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem,
  TextField, ToggleButtonGroup, ToggleButton, CircularProgress, Chip, Container,
} from '@mui/material';
import { Gantt, Task as GanttTaskType, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { getGanttDataAll, GanttTask, GanttAllFilters } from '../services/reportService';
import { getUsers, User } from '../services/userService';
import teamService, { Team } from '../services/teamService';
import api from '../services/api';

const statusColors: Record<string, { bg: string; progress: string }> = {
  todo: { bg: '#94A3B8', progress: '#64748B' },
  in_progress: { bg: '#3B82F6', progress: '#1D4ED8' },
  done: { bg: '#10B981', progress: '#059669' },
};

const priorityColors: Record<number, string> = {
  1: '#10B981', 2: '#3B82F6', 3: '#F59E0B', 4: '#EF4444',
};

function transformToGanttTasks(data: GanttTask[]): GanttTaskType[] {
  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const defaultEnd = new Date(defaultStart.getTime() + 7 * 86400000);

  // Group by project for hierarchy
  const projectMap = new Map<string, GanttTask[]>();
  data.forEach(t => {
    const key = t.projectId;
    if (!projectMap.has(key)) projectMap.set(key, []);
    projectMap.get(key)!.push(t);
  });

  const result: GanttTaskType[] = [];
  let order = 0;

  projectMap.forEach((tasks, projectId) => {
    const projectName = tasks[0]?.projectName || 'Unknown Project';

    // Find tasks with dates to compute project range
    const tasksWithDates = tasks.filter(t => t.startDate || t.dueDate);
    const projectStart = tasksWithDates.length > 0
      ? new Date(Math.min(...tasksWithDates.map(t => new Date(t.startDate || t.dueDate!).getTime())))
      : defaultStart;
    const projectEnd = tasksWithDates.length > 0
      ? new Date(Math.max(...tasksWithDates.map(t => new Date(t.dueDate || t.startDate!).getTime())))
      : defaultEnd;

    // Compute project progress
    const doneTasks = tasks.filter(t => t.statusCategory === 'done').length;
    const projectProgress = tasks.length > 0 ? Math.round((doneTasks / tasks.length) * 100) : 0;

    // Add project row
    result.push({
      id: `project-${projectId}`,
      type: 'project',
      name: projectName,
      start: projectStart,
      end: projectEnd > projectStart ? projectEnd : new Date(projectStart.getTime() + 86400000),
      progress: projectProgress,
      hideChildren: false,
      displayOrder: order++,
      styles: {
        backgroundColor: '#1976d2',
        progressColor: '#0d47a1',
      },
    });

    // Separate parent tasks and subtasks
    const parentTasks = tasks.filter(t => !t.parentTaskId);
    const childMap = new Map<string, GanttTask[]>();
    tasks.filter(t => t.parentTaskId).forEach(t => {
      if (!childMap.has(t.parentTaskId!)) childMap.set(t.parentTaskId!, []);
      childMap.get(t.parentTaskId!)!.push(t);
    });

    parentTasks.forEach(task => {
      const start = task.startDate ? new Date(task.startDate) : defaultStart;
      const end = task.dueDate ? new Date(task.dueDate) : new Date(start.getTime() + 7 * 86400000);
      const cat = task.statusCategory || 'todo';
      const colors = statusColors[cat] || statusColors.todo;

      result.push({
        id: task.id,
        type: 'task',
        name: task.title,
        start,
        end: end > start ? end : new Date(start.getTime() + 86400000),
        progress: task.progress,
        project: `project-${projectId}`,
        dependencies: task.dependencies?.filter(d => tasks.some(t => t.id === d)) || [],
        displayOrder: order++,
        styles: {
          backgroundColor: task.statusColor || colors.bg,
          progressColor: colors.progress,
        },
      });

      // Add subtasks
      const children = childMap.get(task.id) || [];
      children.forEach(sub => {
        const sStart = sub.startDate ? new Date(sub.startDate) : start;
        const sEnd = sub.dueDate ? new Date(sub.dueDate) : new Date(sStart.getTime() + 3 * 86400000);
        const sCat = sub.statusCategory || 'todo';
        const sColors = statusColors[sCat] || statusColors.todo;

        result.push({
          id: sub.id,
          type: 'task',
          name: `  ${sub.title}`,
          start: sStart,
          end: sEnd > sStart ? sEnd : new Date(sStart.getTime() + 86400000),
          progress: sub.progress,
          project: task.id,
          dependencies: sub.dependencies?.filter(d => tasks.some(t => t.id === d)) || [],
          displayOrder: order++,
          styles: {
            backgroundColor: sub.statusColor || sColors.bg,
            progressColor: sColors.progress,
          },
        });
      });
    });
  });

  return result;
}

// Custom tooltip
const CustomTooltip: React.FC<{ task: GanttTaskType; fontSize: string; fontFamily: string }> = ({ task }) => {
  return (
    <Paper sx={{ p: 1.5, maxWidth: 280 }}>
      <Typography variant="subtitle2" fontWeight="bold">{task.name}</Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        {task.start.toLocaleDateString()} - {task.end.toLocaleDateString()}
      </Typography>
      <Typography variant="caption" display="block">
        Progress: {task.progress}%
      </Typography>
      {task.type === 'project' && (
        <Chip label="Project" size="small" color="primary" sx={{ mt: 0.5 }} />
      )}
    </Paper>
  );
};

const GanttPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ganttData, setGanttData] = useState<GanttTask[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Week);

  // Filters
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  const [filterTeam, setFilterTeam] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Load filter data on mount
  useEffect(() => {
    Promise.all([
      api.get('/projects').then(r => setProjects(r.data)).catch(() => {}),
      getUsers().then(u => setUsers(u)).catch(() => {}),
      teamService.getAllTeams().then(t => setTeams(t)).catch(() => {}),
    ]);
  }, []);

  // Load gantt data when filters change
  useEffect(() => {
    loadGanttData();
  }, [filterProject, filterAssignee, filterTeam]);

  const loadGanttData = async () => {
    try {
      setLoading(true);
      const filters: GanttAllFilters = {};
      if (filterProject !== 'all') filters.projectId = filterProject;
      if (filterAssignee !== 'all') filters.assigneeId = filterAssignee;
      if (filterTeam !== 'all') filters.teamId = filterTeam;

      const data = await getGanttDataAll(filters);
      setGanttData(data);
    } catch (err) {
      console.error('Failed to load gantt data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Transform and filter data
  const ganttTasks = useMemo(() => {
    let filtered = ganttData;
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.assigneeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.projectName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return transformToGanttTasks(filtered);
  }, [ganttData, searchTerm]);

  const handleTaskClick = (task: GanttTaskType) => {
    if (!task.id.startsWith('project-')) {
      navigate(`/tasks/${task.id}`);
    }
  };

  const handleExpanderClick = (task: GanttTaskType) => {
    const updated = ganttTasks.map(t =>
      t.id === task.id ? { ...t, hideChildren: !t.hideChildren } : t
    );
    // Force re-render - gantt-task-react handles hide/show via hideChildren
    setGanttData([...ganttData]);
  };

  const columnWidth = viewMode === ViewMode.Month ? 200 : viewMode === ViewMode.Week ? 120 : 50;

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold">Gantt Chart</Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value={ViewMode.Day}>Day</ToggleButton>
          <ToggleButton value={ViewMode.Week}>Week</ToggleButton>
          <ToggleButton value={ViewMode.Month}>Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search tasks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Project</InputLabel>
          <Select value={filterProject} label="Project" onChange={(e) => setFilterProject(e.target.value)}>
            <MenuItem value="all">All Projects</MenuItem>
            {projects.map((p: any) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Assignee</InputLabel>
          <Select value={filterAssignee} label="Assignee" onChange={(e) => setFilterAssignee(e.target.value)}>
            <MenuItem value="all">All Assignees</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Team</InputLabel>
          <Select value={filterTeam} label="Team" onChange={(e) => setFilterTeam(e.target.value)}>
            <MenuItem value="all">All Teams</MenuItem>
            {teams.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {ganttData.length} tasks
        </Typography>
      </Paper>

      {/* Gantt Chart */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : ganttTasks.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No tasks with dates found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Tasks need Start Date and Due Date to appear on the Gantt chart
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflow: 'hidden' }}>
          <Gantt
            tasks={ganttTasks}
            viewMode={viewMode}
            onClick={handleTaskClick}
            onExpanderClick={handleExpanderClick}
            columnWidth={columnWidth}
            listCellWidth="220px"
            rowHeight={40}
            barFill={70}
            fontSize="12px"
            headerHeight={50}
            todayColor="rgba(25, 118, 210, 0.1)"
            arrowColor="#94A3B8"
            arrowIndent={20}
            barCornerRadius={4}
            TooltipContent={CustomTooltip}
            ganttHeight={Math.min(600, ganttTasks.length * 40 + 50)}
          />
        </Paper>
      )}

      {/* Legend */}
      <Paper sx={{ p: 1.5, mt: 2, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" fontWeight={600}>Status:</Typography>
        {[
          ['To Do', '#94A3B8'],
          ['In Progress', '#3B82F6'],
          ['Done', '#10B981'],
        ].map(([label, color]) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 8, bgcolor: color, borderRadius: 0.5 }} />
            <Typography variant="caption">{label}</Typography>
          </Box>
        ))}
        <Typography variant="caption" fontWeight={600} sx={{ ml: 2 }}>Other:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 8, bgcolor: '#1976d2', borderRadius: 0.5 }} />
          <Typography variant="caption">Project</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 2, bgcolor: '#94A3B8', mt: 0.25 }} />
          <Typography variant="caption">Dependency</Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default GanttPage;
